/**
 * Drill-down modal for every PO Line Dashboard figure — the PO-line sibling of
 * the Scheduled Dashboard's ``CurrentWeekDrilldownModal``: same shell (title,
 * count line, Export as Excel or CSV), same category TAB BAR for segment groups (OTD
 * ring → On time / Early / Late; OTIF ring + tile → All / Passed / Pending /
 * Failed; day/week bars → On time / Early / Late / Pending) with the tile's
 * own figures in the tab labels, the same numbered pager, and the same
 * row-level OTIF decision actions (admins only, server-gated).
 *
 * Pages through ``/analytics/po-line-dashboard/drilldown/``; rows are built
 * from the tile's own predicates so the count equals the figure.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  poLineDashboardAPI,
  type POLineDrilldownExportFormat,
  type POLineDrilldownMode,
  type POLineDrilldownRow,
} from '../../../lib/api/po-line-dashboard';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { notifyExportResult, type ExportTruncation } from '../../../lib/exportTruncation';
import ExportTruncationBanner from '../../export/ExportTruncationBanner';
import { RowsSelect } from '../../ui/pagination';
import { DrilldownPager } from './DrilldownPager';
import { POLineDrilldownTable } from './POLineDrilldownTable';
import { MODE_META, getTabGroup, type DrilldownTabContext, type POLineDrilldownFilters } from './poLineDrilldownModes';

export { LineOtifPill } from './POLineDrilldownTable';

/** CR-9078: blob type and the word the toasts use, per export format. */
const EXPORT_FORMAT_META: Record<POLineDrilldownExportFormat, { mime: string; label: string }> = {
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel' },
  csv: { mime: 'text/csv;charset=utf-8', label: 'CSV' },
};

const DEFAULT_PAGE_SIZE = 50;
/** CR-9081: the Rows choices — capped at the endpoint's 200-row page limit. */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export interface POLineDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: POLineDrilldownMode;
  filters: POLineDrilldownFilters;
  /** ISO period start (inclusive). Omit for "all history" (cumulative arrears). */
  periodStart?: string;
  /** ISO period end (inclusive). */
  periodEnd?: string;
  /** Grain of the tile that opened the drill (W3-T7: a week shows no arrears stock). */
  periodGrain?: 'week' | 'month';
  /** Human label for the period, e.g. "This week", "Mar". */
  periodLabel?: string;
  /** ISO currency code every value column is in (from the dashboard payload). */
  currency: string;
  /**
   * Tile figures per mode — rendered in the sibling tab labels so the tab reads
   * "Passed (12)" with the exact number the ring showed. Omit for no counts.
   */
  tabCounts?: Partial<Record<POLineDrilldownMode, number>>;
  /** ``'bars'`` — day/week bar segments (On time / Early / Late / Pending);
   *  ``'otd_mix'`` — the OTD ring (no Pending). OTIF modes need no context. */
  tabContext?: DrilldownTabContext;
  /** Fired after a row-level OTIF decision so the dashboard can refresh its tiles. */
  onDataChanged?: () => void;
}

export function POLineDrilldownModal({
  open, onClose, mode, filters, periodStart, periodEnd, periodGrain, periodLabel, currency,
  tabCounts, tabContext, onDataChanged,
}: POLineDrilldownModalProps) {
  const { formatDate } = useFormatters();

  // The active category — starts at the clicked mode, switchable via the tab bar.
  const [activeMode, setActiveMode] = useState<POLineDrilldownMode>(mode);
  useEffect(() => { if (open) setActiveMode(mode); }, [open, mode]);
  const meta = MODE_META[activeMode];
  const tabGroup = useMemo(() => getTabGroup(mode, tabContext), [mode, tabContext]);

  const [rows, setRows] = useState<POLineDrilldownRow[]>([]);
  const [total, setTotal] = useState(0);
  // Mixed-grain group: schedule sites counted in the tile but not listed here.
  const [scheduleSites, setScheduleSites] = useState<Array<{ id: number; name: string }>>([]);
  const [page, setPage] = useState(1);
  // CR-9081: the Rows control and row selection of the other drill-down tables.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [ordering, setOrdering] = useState<string>(meta.defaultOrdering);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // CR-7432: counts from the last export, when it was cut short by the row cap.
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);
  // Bumped after an OTIF decision so the current page re-reads its rows.
  const [refreshTick, setRefreshTick] = useState(0);

  // Reset paging/sort whenever the figure behind the modal changes.
  useEffect(() => {
    setPage(1);
    setOrdering(MODE_META[activeMode].defaultOrdering);
    // CR-7432: the banner names a count for the cohort it was exported from.
    // Once the cohort moves, those numbers describe a file nobody is looking
    // at any more — a screen stating something untrue, which is the whole
    // complaint this ticket started from.
    setTruncation(null);
  }, [activeMode, periodStart, periodEnd, filters.customer_id, filters.supplier_id, filters.part_number]);

  const baseParams = useMemo(() => ({
    mode: activeMode,
    customer_id: filters.customer_id,
    supplier_id: filters.supplier_id,
    part_number: filters.part_number,
    start: periodStart,
    end: periodEnd,
    grain: periodGrain,
    ordering,
  }), [activeMode, filters.customer_id, filters.supplier_id, filters.part_number, periodStart, periodEnd, periodGrain, ordering]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    poLineDashboardAPI
      // CR-8673: after an Override / Acknowledge the refetch must not be the
      // browser's 60s-cached copy of the same URL (the row kept its buttons).
      .drilldown({ ...baseParams, page, page_size: pageSize, ...(refreshTick > 0 ? { _r: refreshTick } : {}) })
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.data.results);
        setTotal(resp.data.count);
        setScheduleSites(resp.data.schedule_sites_excluded ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('PO line drilldown failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load rows');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, baseParams, page, pageSize, refreshTick]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // A selection belongs to the rows on screen: a new page, size, sort, tab or
  // re-read clears it.
  useEffect(() => { setSelectedIds(new Set()); }, [rows, page, pageSize, baseParams]);
  const handleToggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) => (rows.length > 0 && rows.every((r) => prev.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id))));
  }, [rows]);
  const handlePageSizeChange = useCallback((size: number) => {
    setPage(1);
    setPageSize(size);
  }, []);

  const handleHeaderClick = useCallback((sortKey: string | null) => {
    if (!sortKey) return;
    setPage(1);
    setOrdering((cur) => (cur === sortKey ? `-${sortKey}` : sortKey));
  }, []);

  // After an override / acknowledge / reset: re-read this page (the row's pill
  // and the "All/Passed/Failed" membership change) and tell the dashboard to
  // refresh its tiles so the ring and the tab counts move together.
  const handleOtifDecision = useCallback(() => {
    setRefreshTick((t) => t + 1);
    onDataChanged?.();
  }, [onDataChanged]);

  // CR-9078: Excel or CSV of the same rows — the server builds both from one pass.
  const handleExport = useCallback(async (fileFormat: POLineDrilldownExportFormat) => {
    const { mime, label } = EXPORT_FORMAT_META[fileFormat];
    setExporting(true);
    try {
      const resp = await poLineDashboardAPI.drilldownExport(baseParams, fileFormat);
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const window = periodStart && periodEnd ? `_${periodStart}_${periodEnd}` : '';
      a.download = `po_line_dashboard_${activeMode}${window}.${fileFormat}`;
      a.click();
      URL.revokeObjectURL(url);
      // CR-7432: "Excel export ready" was said whether or not the file held
      // every row, which is the one thing the user could not check for
      // themselves. The server now says; this reports what it said.
      setTruncation(notifyExportResult(resp, `${label} export ready`));
    } catch (err: unknown) {
      console.error('PO line drilldown export failed:', err);
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(false);
    }
  }, [baseParams, activeMode, periodStart, periodEnd]);

  const description = useMemo(() => {
    const parts = [meta.description];
    if (periodLabel) parts.push(periodLabel);
    else if (periodStart && periodEnd) parts.push(`${formatDate(periodStart)} – ${formatDate(periodEnd)}`);
    else if (periodEnd) parts.push(`up to ${formatDate(periodEnd)}`);
    if (filters.part_number) parts.push(`part ${filters.part_number}`);
    if (filters.supplier_id) parts.push(`supplier #${filters.supplier_id}`);
    parts.push(`${total.toLocaleString()} row${total === 1 ? '' : 's'}`);
    return parts.join(' • ');
  }, [meta.description, periodLabel, periodStart, periodEnd, filters.part_number, filters.supplier_id, total, formatDate]);

  // CR-7270: a past month's Arrears slice is a frozen month-end balance, and
  // these rows carry the values captured then. Keyed on the rows themselves, so
  // it cannot appear over a live grid. The denominator grids (both rings' Due,
  // OTD's Not on time) are MIXED — frozen arrears members beside lines that
  // settled inside the month — so their label says which rows are cached rather
  // than claiming the whole grid is.
  const frozenAsAt = rows
    .map((row) => (row as { frozen_as_at?: string | null }).frozen_as_at)
    .find((value): value is string => Boolean(value));
  const frozenLabel = activeMode === 'otif_arrears'
    ? 'Cached month-end data as at'
    : 'Arrears rows are cached month-end data as at';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="!max-w-[min(1480px,95vw)] w-[min(1480px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{meta.title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
              {frozenAsAt && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  <Archive className="h-3.5 w-3.5" />
                  {frozenLabel} {formatDate(frozenAsAt)}
                </span>
              )}
            </div>
            {/* CR-9078: Excel or CSV, the same pair the call-off and stock
                planning exports offer. mr-6 keeps it clear of the dialog's close button. */}
            <div className="flex items-center gap-2 shrink-0">
              {selectedIds.size > 0 && (
                <span className="text-sm text-muted-foreground" data-testid="pld-selected-count">{selectedIds.size} selected</span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="mr-6 shrink-0" disabled={exporting || total === 0}>
                    {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    Export
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem disabled={exporting} onClick={() => handleExport('xlsx')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                    Download Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={exporting} onClick={() => handleExport('csv')}>
                    <FileText className="h-4 w-4 mr-2 text-blue-700" />
                    Download CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <ExportTruncationBanner
            truncation={truncation}
            onDismiss={() => setTruncation(null)}
          />
        </DialogHeader>

        {/* Category tabs — only for segment groups (bars / OTD ring / OTIF ring + tile). */}
        {tabGroup && (
          <div className="flex flex-wrap gap-1 border-b pb-2" role="tablist" aria-label="Drill-down categories">
            {tabGroup.map((t) => {
              const active = t.mode === activeMode;
              const count = tabCounts?.[t.mode];
              return (
                <button
                  key={t.mode}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveMode(t.mode)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t.label}{count !== undefined ? <span className="ml-1 tabular-nums opacity-80">({count.toLocaleString()})</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {scheduleSites.length > 0 && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="drilldown-schedule-sites">
            Listing PO-line sites only. {scheduleSites.map((s) => s.name).join(', ')} {scheduleSites.length === 1 ? 'is' : 'are'} judged
            on schedule lines and counted in the figure; {scheduleSites.length === 1 ? 'its' : 'their'} rows are on the Scheduled tab.
          </div>
        )}
        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : total === 0 && !loading ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No rows match this figure.</div>
        ) : (
          <POLineDrilldownTable
            entity={meta.entity}
            rows={rows}
            ordering={ordering}
            onHeaderClick={handleHeaderClick}
            currency={currency}
            onOtifDecision={handleOtifDecision}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
          />
        )}

        {/* CR-9081: table options on every populated grid — Rows, the page
            summary of the other drill-downs, and the numbered pager with its
            Go-to-page jump when there is more than one page. */}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2" data-testid="pld-table-options">
            <div className="flex items-center gap-3 text-sm text-foreground">
              <RowsSelect value={pageSize} options={PAGE_SIZE_OPTIONS} onChange={handlePageSizeChange} />
              <span>
                Page {page.toLocaleString()} of {totalPages.toLocaleString()} • {total.toLocaleString()} total records
                <span className="ml-2 text-xs">
                  (Showing {((page - 1) * pageSize + 1).toLocaleString()}-{Math.min(page * pageSize, total).toLocaleString()})
                </span>
              </span>
            </div>
            {totalPages > 1 && (
              <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
